// ============================================================================
// Landing.jsx — Repeaked
// Drop into src/pages/Landing.js (CRA accepts .jsx and .js interchangeably).
// Inline styles + state-driven animations to match project convention.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------
const C = {
  red:        '#E8192C',
  redDim:     '#a4111d',
  redGlow:    'rgba(232, 25, 44, 0.45)',
  orange:     '#FF6B2B',
  bg:         '#0C0905',
  bgWarm:     '#100B07',
  bgWarmer:   '#15100B',
  text:       '#F0F2F5',
  textDim:    '#9A938B',
  textVeryDim:'#5C554E',
  card:       '#111111',
  cardAlt:    '#16110D',
  border:     '#1e1e1e',
  borderBright:'#2A2520',
  greenLive:  '#3FCB7E',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body:    '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const DISCORD_URL = 'https://discord.gg/CcpV64MAPW';
const TWITTER_URL = 'https://twitter.com/RepeakedCSGO';

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
const useIsMobile = (bp = 860) => {
  const [m, setM] = useState(
    typeof window !== 'undefined' ? window.innerWidth < bp : false
  );
  useEffect(() => {
    const check = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [bp]);
  return m;
};

const useHover = () => {
  const [h, setH] = useState(false);
  return [
    h,
    {
      onMouseEnter: () => setH(true),
      onMouseLeave: () => setH(false),
    },
  ];
};

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------
const RANKS = [
  { id: 1,  name: 'Rust',                    range: '0–400',     image: 'rust.png'                    },
  { id: 2,  name: 'Rust Elite',              range: '401–700',   image: 'rust-elite.png'              },
  { id: 3,  name: 'Shadow Nova',             range: '701–1000',  image: 'shadow-nova.png'             },
  { id: 4,  name: 'Shadow Nova Master',      range: '1001–1300', image: 'shadow-nova-master.png'      },
  { id: 5,  name: 'Phantom Guardian',        range: '1301–1550', image: 'phantom-guardian.png'        },
  { id: 6,  name: 'Phantom Guardian Elite',  range: '1551–1800', image: 'phantom-guardian-elite.png'  },
  { id: 7,  name: 'Distinguished Phantom',   range: '1801–2000', image: 'distinguished-phantom.png'   },
  { id: 8,  name: 'Legendary Wraith',        range: '2001–2200', image: 'legendary-wraith.png'        },
  { id: 9,  name: 'Legendary Wraith Master', range: '2201–2500', image: 'legendary-wraith-master.png' },
  { id: 10, name: 'Supreme Legacy',          range: '2501–2750', image: 'supreme-legacy.png'          },
  { id: 11, name: 'Repeaked',                range: '2751–3000', image: 'repeaked.png'                },
];

const FEATURES = [
  {
    title: '128-TICK SERVERS',
    body:  'Hit registration the way you remember it. Hetzner-grade hardware in low-ping EU regions, spun up per match.',
  },
  {
    title: 'ELO RANKING',
    body:  'Eleven ranks, 0 to 3000. K-factor tuned for serious play. Win-streak weighting. Smurf protection coming.',
  },
  {
    title: 'SKIN ECONOMY',
    body:  'Earn Fragz from matches, open crates, apply StatTrak and Nametags. Real progression — not pay-to-equip.',
  },
  {
    title: '5V5 + WINGMAN',
    body:  'Competitive 5v5 with full veto. 2v2 Wingman across the Wingman map pool. Both queues, always open.',
  },
  {
    title: 'CUSTOM LOBBIES',
    body:  'Public or private. Bots on either side. Knife rounds, all-talk, team locks, region pick — your rules.',
  },
  {
    title: 'FREE FOREVER',
    body:  'Matchmaking is free. No paywall on ranking up. Premium Pass is optional cosmetics, not a competitive tax.',
  },
];

const CRATES = [
  { label: 'MIL-SPEC',      tint: '#4B69A8', price: '100 – 300 FRAGZ'     },
  { label: 'RESTRICTED',    tint: '#8847FF', price: '650 – 950 FRAGZ'     },
  { label: 'CLASSIFIED',    tint: '#D32CE6', price: '1,400 – 2,000 FRAGZ' },
  { label: 'COVERT',        tint: '#EB4B4B', price: '3,500 – 5,000 FRAGZ' },
  { label: 'EXTRAORDINARY', tint: C.orange,  price: '5,500 – 7,000 FRAGZ' },
];

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
const Diamond = ({ size = 16, color = C.red, dotColor = C.orange, withDot = false, hollow = true, style }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      border: `${Math.max(1.5, size / 9)}px solid ${color}`,
      background: hollow ? 'transparent' : color,
      transform: 'rotate(45deg)',
      position: 'relative',
      boxSizing: 'border-box',
      flexShrink: 0,
      ...style,
    }}
  >
    {withDot && (
      <span
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: size * 0.32,
          height: size * 0.32,
          background: dotColor,
          transform: 'translate(-50%, -50%)',
        }}
      />
    )}
  </span>
);

const LogoLockup = ({ size = 22 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.6 }}>
    <Diamond size={size} withDot />
    <span
      style={{
        fontFamily: F.display,
        fontSize: size * 1.1,
        letterSpacing: 2,
        lineHeight: 1,
        color: C.text,
      }}
    >
      RE<span style={{ color: C.red }}>PEAKED</span>
    </span>
  </div>
);

// Clickable home link — wraps LogoLockup with hover state + cursor
const LogoButton = ({ onClick, size = 20 }) => {
  const [hover, hp] = useHover();
  return (
    <button
      onClick={onClick}
      aria-label="Repeaked home"
      {...hp}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        opacity: hover ? 1 : 0.95,
        transform: hover ? 'translateY(-1px)' : 'translateY(0)',
        transition: 'opacity 160ms ease, transform 160ms ease',
      }}
    >
      <LogoLockup size={size} />
    </button>
  );
};

const Eyebrow = ({ children, color = C.red, style }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      fontFamily: F.body,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 3,
      textTransform: 'uppercase',
      color: C.textDim,
      ...style,
    }}
  >
    <Diamond size={8} color={color} hollow={false} />
    <span>{children}</span>
  </div>
);

const StatusDot = ({ color = C.greenLive, label }) => {
  const [on, setOn] = useState(true);
  useEffect(() => {
    const i = setInterval(() => setOn(o => !o), 900);
    return () => clearInterval(i);
  }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          boxShadow: on ? `0 0 0 0 ${color}55, 0 0 12px ${color}` : `0 0 0 6px ${color}00, 0 0 4px ${color}66`,
          transition: 'box-shadow 900ms ease-out',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          fontFamily: F.body,
          fontSize: 11,
          letterSpacing: 2.5,
          textTransform: 'uppercase',
          color: C.text,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
    </div>
  );
};

const PrimaryButton = ({ children, onClick, size = 'md', style, ariaLabel }) => {
  const [hover, hp] = useHover();
  const [active, setActive] = useState(false);
  const sizes = {
    sm: { px: 18, py: 10, fs: 14, ls: 2 },
    md: { px: 28, py: 14, fs: 16, ls: 2.5 },
    lg: { px: 40, py: 20, fs: 22, ls: 3 },
  };
  const sz = sizes[size];
  return (
    <button
      onClick={onClick}
      aria-label={ariaLabel}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      {...hp}
      style={{
        fontFamily: F.display,
        fontSize: sz.fs,
        letterSpacing: sz.ls,
        padding: `${sz.py}px ${sz.px}px`,
        background: hover ? C.red : C.red,
        color: C.text,
        border: 'none',
        borderRadius: 2,
        cursor: 'pointer',
        textTransform: 'uppercase',
        transform: active ? 'translateY(1px)' : hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? `0 8px 20px -8px ${C.redGlow}, 0 0 0 1px ${C.orange}33` : `0 4px 12px -6px ${C.redGlow}`,
        transition: 'transform 160ms ease, box-shadow 200ms ease, background 160ms ease',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </button>
  );
};

const SecondaryButton = ({ children, onClick, size = 'md', style, href }) => {
  const [hover, hp] = useHover();
  const sizes = {
    sm: { px: 18, py: 10, fs: 14, ls: 2 },
    md: { px: 26, py: 13, fs: 16, ls: 2.5 },
    lg: { px: 38, py: 19, fs: 22, ls: 3 },
  };
  const sz = sizes[size];
  const styleObj = {
    fontFamily: F.display,
    fontSize: sz.fs,
    letterSpacing: sz.ls,
    padding: `${sz.py}px ${sz.px}px`,
    background: hover ? C.text : 'transparent',
    color: hover ? C.bg : C.text,
    border: `1.5px solid ${hover ? C.text : C.borderBright}`,
    borderRadius: 2,
    cursor: 'pointer',
    textTransform: 'uppercase',
    transition: 'background 160ms ease, color 160ms ease, border-color 160ms ease, transform 160ms ease',
    transform: hover ? 'translateY(-2px)' : 'translateY(0)',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    ...style,
  };
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...hp} style={styleObj}>
        {children}
      </a>
    );
  }
  return (
    <button onClick={onClick} {...hp} style={styleObj}>
      {children}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Glyphs — small SVG icons in the diamond/bracket vocabulary
// ---------------------------------------------------------------------------
const Glyph = ({ name, size = 36, color = C.red }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 36 36',
    fill: 'none',
    stroke: color,
    strokeWidth: 2,
    strokeLinecap: 'square',
    strokeLinejoin: 'miter',
  };
  switch (name) {
    case 'tick':
      return (
        <svg {...common}>
          <rect x="4" y="4" width="28" height="28" />
          <path d="M10 19 L16 25 L26 12" stroke={C.orange} />
        </svg>
      );
    case 'ladder':
      return (
        <svg {...common}>
          <path d="M8 30 L8 6 M20 30 L20 12 M32 30 L32 18" />
          <path d="M2 30 L36 30" stroke={C.orange} />
        </svg>
      );
    case 'diamond':
      return (
        <svg {...common}>
          <path d="M18 4 L32 18 L18 32 L4 18 Z" />
          <circle cx="18" cy="18" r="3" fill={C.orange} stroke="none" />
        </svg>
      );
    case 'split':
      return (
        <svg {...common}>
          <path d="M4 8 L16 8 L16 28 L4 28 Z" />
          <path d="M20 8 L32 8 L32 28 L20 28 Z" stroke={C.orange} />
        </svg>
      );
    case 'bracket':
      return (
        <svg {...common}>
          <path d="M10 4 L4 4 L4 32 L10 32" />
          <path d="M26 4 L32 4 L32 32 L26 32" />
          <circle cx="18" cy="18" r="2" fill={C.orange} stroke="none" />
        </svg>
      );
    case 'cross':
      return (
        <svg {...common}>
          <path d="M18 4 L18 32 M4 18 L32 18" />
          <rect x="14" y="14" width="8" height="8" stroke={C.orange} />
        </svg>
      );
    case 'discord':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.073-.035 19.74 19.74 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.01c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
      );
    case 'x':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      );
    case 'arrow':
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
          <path d="M4 12h16M14 6l6 6-6 6" strokeLinecap="square" />
        </svg>
      );
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Background drift — diamonds float behind hero
// ---------------------------------------------------------------------------
const DriftField = ({ count = 9, fixed = false }) => {
  const [diamonds, setDiamonds] = useState(() =>
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 24 + Math.random() * 70,
      rot: Math.random() * 360,
      vx: (Math.random() - 0.5) * 0.04,
      vy: (Math.random() - 0.5) * 0.03 - 0.005,
      vrot: (Math.random() - 0.5) * 0.15,
      opacity: 0.04 + Math.random() * 0.08,
      color: Math.random() > 0.8 ? C.orange : C.red,
    }))
  );

  useEffect(() => {
    let raf;
    let lastTick = performance.now();
    const tick = (now) => {
      const dt = Math.min(50, now - lastTick); // clamp on tab refocus
      lastTick = now;
      const step = dt / 16.67; // normalize to 60fps
      setDiamonds(prev =>
        prev.map(d => {
          let nx = d.x + d.vx * step;
          let ny = d.y + d.vy * step;
          if (nx > 110) nx = -10;
          if (nx < -10) nx = 110;
          if (ny > 110) ny = -10;
          if (ny < -10) ny = 110;
          return { ...d, x: nx, y: ny, rot: d.rot + d.vrot * step };
        })
      );
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: fixed ? 'fixed' : 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: fixed ? 1 : 0,
      }}
    >
      {diamonds.map(d => (
        <span
          key={d.id}
          style={{
            position: 'absolute',
            left: `${d.x}%`,
            top: `${d.y}%`,
            width: d.size,
            height: d.size,
            border: `1.5px solid ${d.color}`,
            opacity: d.opacity,
            transform: `translate(-50%, -50%) rotate(${d.rot}deg)`,
            boxSizing: 'border-box',
          }}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grain — paper-noise overlay
// ---------------------------------------------------------------------------
const Grain = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 9999,
      opacity: 0.05,
      mixBlendMode: 'overlay',
      backgroundImage:
        'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix values=%220 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
      backgroundSize: '160px 160px',
    }}
  />
);

// ---------------------------------------------------------------------------
// StatPill
// ---------------------------------------------------------------------------
const StatPill = ({ value, label, accent = false }) => (
  <div
    style={{
      padding: '20px 22px',
      background: accent ? `linear-gradient(180deg, ${C.cardAlt}, ${C.card})` : C.card,
      border: `1px solid ${accent ? C.borderBright : C.border}`,
      borderLeft: `2px solid ${accent ? C.red : C.borderBright}`,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontFamily: F.display,
        fontSize: 38,
        letterSpacing: 1,
        lineHeight: 1,
        color: C.text,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontFamily: F.body,
        fontSize: 11,
        letterSpacing: 2.5,
        textTransform: 'uppercase',
        color: C.textDim,
        fontWeight: 600,
      }}
    >
      {label}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// FeatureCard
// ---------------------------------------------------------------------------
const FeatureCard = ({ title, body, index = 0 }) => {
  const [hover, hp] = useHover();
  return (
    <div
      {...hp}
      style={{
        position: 'relative',
        padding: 32,
        background: hover ? C.cardAlt : C.card,
        border: `1px solid ${hover ? C.red : C.border}`,
        transition: 'background 200ms ease, border-color 200ms ease, transform 200ms ease',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        cursor: 'default',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          fontFamily: F.display,
          fontSize: 52,
          color: C.red,
          lineHeight: 1,
          marginBottom: 20,
          letterSpacing: -1,
          opacity: 0.85,
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </div>
      <div
        style={{
          fontFamily: F.display,
          fontSize: 28,
          letterSpacing: 1.5,
          color: C.text,
          marginBottom: 12,
          lineHeight: 1,
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: F.body,
          fontSize: 15,
          lineHeight: 1.55,
          color: C.textDim,
          fontWeight: 500,
        }}
      >
        {body}
      </div>
      <div
        style={{
          position: 'absolute',
          right: 18,
          bottom: 18,
          opacity: hover ? 1 : 0,
          transition: 'opacity 220ms ease',
        }}
      >
        <Diamond size={10} color={C.orange} hollow={false} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// RankBadge
// ---------------------------------------------------------------------------
const RankBadge = ({ rank, isMax }) => {
  const [hover, hp] = useHover();
  const [imgOk, setImgOk] = useState(true);
  return (
    <div
      {...hp}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        padding: '16px 4px',
        flex: 1,
        minWidth: 0,
        cursor: 'default',
        transition: 'transform 220ms ease',
        transform: hover ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      <div
        style={{
          width: 64,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 10,
          filter: isMax ? `drop-shadow(0 0 12px ${C.redGlow})` : 'none',
          transition: 'filter 220ms ease',
        }}
      >
        {imgOk ? (
          <img
            src={`/assets/ranks/${rank.image}`}
            alt={rank.name}
            onError={() => setImgOk(false)}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <div
            style={{
              width: 52,
              height: 58,
              clipPath: 'polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)',
              background: isMax ? C.red : C.cardAlt,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: F.display,
              fontSize: 22,
              color: C.text,
            }}
          >
            {rank.id}
          </div>
        )}
      </div>
      <div
        style={{
          fontFamily: F.display,
          fontSize: 10,
          letterSpacing: 2,
          color: C.textVeryDim,
          marginBottom: 2,
          whiteSpace: 'nowrap',
        }}
      >
        {String(rank.id).padStart(2, '0')}
      </div>
      <div
        style={{
          fontFamily: F.display,
          fontSize: 11,
          letterSpacing: 0.8,
          color: isMax ? C.red : C.text,
          lineHeight: 1.1,
          marginBottom: 4,
          wordBreak: 'break-word',
          hyphens: 'auto',
        }}
      >
        {rank.name}
      </div>
      <div
        style={{
          fontFamily: F.body,
          fontSize: 10,
          letterSpacing: 1,
          color: C.textDim,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {rank.range}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// CrateTierRow — compact horizontal row for economy section
// ---------------------------------------------------------------------------
const CrateTierRow = ({ crate, last }) => {
  const [hover, hp] = useHover();
  return (
    <div
      {...hp}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '18px 24px',
        background: hover ? C.cardAlt : C.card,
        borderBottom: last ? 'none' : `1px solid ${C.border}`,
        transition: 'background 160ms ease',
        gap: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span
          style={{
            display: 'inline-block',
            width: 10,
            height: 10,
            background: crate.tint,
            transform: 'rotate(45deg)',
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontFamily: F.display,
            fontSize: 20,
            letterSpacing: 2,
            color: hover ? C.text : C.textDim,
            transition: 'color 160ms ease',
          }}
        >
          {crate.label}
        </span>
      </div>
      <span
        style={{
          fontFamily: F.body,
          fontSize: 13,
          letterSpacing: 1.5,
          color: C.textVeryDim,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {crate.price}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SocialCard
// ---------------------------------------------------------------------------
const SocialCard = ({ platform, headline, body, cta, href, accent }) => {
  const [hover, hp] = useHover();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...hp}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        padding: 36,
        background: hover ? C.cardAlt : C.card,
        borderTop: `1px solid ${hover ? accent : C.border}`,
        borderRight: `1px solid ${hover ? accent : C.border}`,
        borderBottom: `1px solid ${hover ? accent : C.border}`,
        borderLeft: `3px solid ${accent}`,
        textDecoration: 'none',
        transition: 'background 200ms ease, border-color 200ms ease, transform 200ms ease',
        transform: hover ? 'translateY(-3px)' : 'translateY(0)',
        flex: 1,
        minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <Glyph name={platform} size={28} color={accent} />
        <span
          style={{
            fontFamily: F.body,
            fontSize: 11,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: C.textDim,
            fontWeight: 600,
          }}
        >
          {platform === 'discord' ? 'DISCORD' : 'X / TWITTER'}
        </span>
      </div>
      <div
        style={{
          fontFamily: F.display,
          fontSize: 28,
          letterSpacing: 1.2,
          color: C.text,
          lineHeight: 1.1,
        }}
      >
        {headline}
      </div>
      <div
        style={{
          fontFamily: F.body,
          fontSize: 15,
          color: C.textDim,
          lineHeight: 1.5,
          flex: 1,
        }}
      >
        {body}
      </div>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: F.display,
          fontSize: 14,
          letterSpacing: 2.5,
          color: hover ? accent : C.text,
          textTransform: 'uppercase',
          transition: 'color 180ms ease',
        }}
      >
        {cta}
        <span style={{ transform: hover ? 'translateX(4px)' : 'translateX(0)', transition: 'transform 200ms ease' }}>
          →
        </span>
      </div>
    </a>
  );
};

// ===========================================================================
// MAIN COMPONENT
// ===========================================================================
export default function Landing() {
  const navigate = useNavigate();
  const isMobile = useIsMobile(860);
  const isSmall = useIsMobile(560);
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(18px)',
    transition: `opacity 700ms ease ${delay}ms, transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1) ${delay}ms`,
  });

  // ----------------------------------------------------------------------
  // Styles (page-level shells)
  // ----------------------------------------------------------------------
  const s = {
    page: {
      background: C.bg,
      color: C.text,
      fontFamily: F.body,
      minHeight: '100vh',
      overflowX: 'hidden',
      position: 'relative',
      animation: 'rp-page-up 420ms cubic-bezier(0.2, 0.7, 0.2, 1) both',
    },
    // Nav
    nav: {
      position: 'sticky',
      top: 0,
      zIndex: 100,
      background: scrolled ? 'rgba(12, 9, 5, 0.85)' : 'rgba(12, 9, 5, 0.4)',
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      borderBottom: `1px solid ${scrolled ? C.borderBright : 'transparent'}`,
      transition: 'background 200ms ease, border-color 200ms ease',
    },
    navInner: {
      maxWidth: 1280,
      margin: '0 auto',
      padding: isSmall ? '14px 20px' : '16px 32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 24,
    },
    navLinks: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: 36,
    },
    navLink: {
      fontFamily: F.body,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 2.5,
      textTransform: 'uppercase',
      color: C.textDim,
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 160ms ease',
    },
    section: {
      maxWidth: 1280,
      margin: '0 auto',
      padding: isSmall ? '80px 20px' : '120px 32px',
      position: 'relative',
    },
    // Section heading set
    sectionHeading: {
      fontFamily: F.display,
      fontSize: isSmall ? 44 : isMobile ? 58 : 76,
      letterSpacing: 1.5,
      lineHeight: 0.95,
      color: C.text,
      margin: '14px 0 0 0',
    },
    sectionSubhead: {
      fontFamily: F.body,
      fontSize: isSmall ? 15 : 17,
      color: C.textDim,
      maxWidth: 640,
      lineHeight: 1.55,
      marginTop: 20,
    },
  };

  // ----------------------------------------------------------------------
  return (
    <div style={s.page}>
      {/* Page-level styles we can't do inline */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@400;500;600;700&display=swap');
        html, body {
          background: ${C.bg};
          margin: 0;
          padding: 0;
          height: auto;
          min-height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          scroll-behavior: smooth;
        }
        @keyframes rp-page-up {
          from { opacity: 0; transform: translateY(18px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 1ms !important; transition-duration: 1ms !important; }
        }
        #root { min-height: 100%; height: auto; overflow: visible; }
        * { box-sizing: border-box; }
        ::selection { background: ${C.red}; color: ${C.text}; }
        a:focus-visible, button:focus-visible {
          outline: 2px solid ${C.orange};
          outline-offset: 3px;
        }
        .repeaked-scroll-x { scrollbar-width: thin; scrollbar-color: ${C.borderBright} transparent; }
        .repeaked-scroll-x::-webkit-scrollbar { height: 6px; }
        .repeaked-scroll-x::-webkit-scrollbar-track { background: transparent; }
        .repeaked-scroll-x::-webkit-scrollbar-thumb { background: ${C.borderBright}; border-radius: 3px; }
      `}</style>

      <Grain />
      <DriftField count={18} fixed />

      {/* Content sits above the fixed diamond layer */}
      <div style={{ position: 'relative', zIndex: 2 }}>

      {/* ============================================================ */}
      {/* NAV */}
      {/* ============================================================ */}
      <nav style={s.nav}>
        <div style={s.navInner}>
          <LogoButton onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          <div style={s.navLinks}>
            {[
              ['Features', '#features'],
              ['Ranks', '#ranks'],
              ['Economy', '#economy'],
              ['Community', '#community'],
            ].map(([label, href]) => (
              <NavLink key={href} href={href} label={label} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isSmall ? 10 : 14 }}>
            {!isSmall && (
              <span
                onClick={() => navigate('/login')}
                style={{
                  fontFamily: F.body,
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: 2.5,
                  textTransform: 'uppercase',
                  color: C.textDim,
                  cursor: 'pointer',
                  padding: '8px 4px',
                }}
              >
                Log In
              </span>
            )}
            <PrimaryButton size="sm" onClick={() => navigate('/register')}>
              Play Free
            </PrimaryButton>
          </div>
        </div>
      </nav>

      {/* ============================================================ */}
      {/* HERO */}
      {/* ============================================================ */}
      <section
        style={{
          position: 'relative',
          minHeight: isSmall ? 'auto' : '92vh',
          padding: isSmall ? '60px 20px 80px' : '80px 32px 120px',
          background: `
            radial-gradient(circle at 20% 15%, rgba(232, 25, 44, 0.12), transparent 50%),
            radial-gradient(circle at 85% 80%, rgba(255, 107, 43, 0.07), transparent 55%)
          `,
        }}
      >

        <div
          style={{
            position: 'relative',
            zIndex: 2,
            maxWidth: 1280,
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr',
            gap: isMobile ? 48 : 64,
            alignItems: 'center',
            minHeight: isSmall ? 'auto' : 'calc(92vh - 200px)',
            paddingTop: isMobile ? 40 : 60,
          }}
        >
          {/* LEFT */}
          <div>
            <div style={fadeUp(0)}>
              <Eyebrow>CS:GO Legacy · Competitive · Free</Eyebrow>
            </div>

            <h1
              style={{
                fontFamily: F.display,
                margin: '32px 0 0 0',
                lineHeight: 0.88,
                letterSpacing: -0.5,
                ...fadeUp(80),
              }}
            >
              <span
                style={{
                  display: 'block',
                  fontSize: isSmall ? 28 : isMobile ? 36 : 44,
                  letterSpacing: 4,
                  color: C.textDim,
                  fontWeight: 400,
                  marginBottom: isSmall ? 6 : 10,
                }}
              >
                THE RETURN TO
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: isSmall ? 84 : isMobile ? 120 : 168,
                  letterSpacing: -2,
                  color: C.text,
                  lineHeight: 0.86,
                }}
              >
                PEAK CS<span style={{ color: C.red }}>.</span>
              </span>
            </h1>

            <p
              style={{
                ...fadeUp(180),
                margin: '32px 0 0 0',
                fontFamily: F.body,
                fontSize: isSmall ? 16 : 19,
                lineHeight: 1.5,
                color: C.textDim,
                maxWidth: 540,
                fontWeight: 500,
              }}
            >
              Free 128-tick matchmaking, ELO ranks, and a real skin economy. Built for the players who didn't follow Valve to CS2.
            </p>

            <div
              style={{
                ...fadeUp(280),
                display: 'flex',
                flexWrap: 'wrap',
                gap: 14,
                marginTop: 36,
              }}
            >
              <PrimaryButton size={isSmall ? 'md' : 'lg'} onClick={() => navigate('/register')}>
                Play Free <span style={{ marginLeft: 4 }}>→</span>
              </PrimaryButton>
              <SecondaryButton size={isSmall ? 'md' : 'lg'} href={DISCORD_URL}>
                Join Discord <span style={{ marginLeft: 4, fontSize: '0.75em' }}>↗</span>
              </SecondaryButton>
            </div>
          </div>

          {/* RIGHT */}
          <div style={fadeUp(380)}>
            <div style={{ marginBottom: 22 }}>
              <StatusDot label="All servers online" />
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: isSmall ? 10 : 14,
                maxWidth: isMobile ? '100%' : 420,
              }}
            >
              <StatPill value="128" label="Tick rate" accent />
              <StatPill value="FREE" label="Forever" />
              <StatPill value="11" label="Ranks" />
              <StatPill value="2" label="Modes · 5v5 + WM" accent />
            </div>
          </div>
        </div>

        {/* Scroll cue */}
        {!isSmall && (
          <button
            onClick={() => {
              const target = document.getElementById('features');
              if (target) {
                const y = target.getBoundingClientRect().top + window.scrollY - 70;
                window.scrollTo({ top: y, behavior: 'smooth' });
              } else {
                window.scrollBy({ top: window.innerHeight * 0.9, behavior: 'smooth' });
              }
            }}
            aria-label="Scroll down"
            style={{
              ...fadeUp(600),
              position: 'absolute',
              bottom: 28,
              left: '50%',
              transform: mounted ? 'translateX(-50%)' : 'translateX(-50%) translateY(8px)',
              opacity: mounted ? 0.6 : 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              fontFamily: F.body,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: 'uppercase',
              color: C.textDim,
              zIndex: 2,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '8px 16px',
            }}
          >
            <Diamond size={8} color={C.textDim} hollow={false} />
            <span>Scroll</span>
          </button>
        )}
      </section>

      {/* ============================================================ */}
      {/* MANIFESTO */}
      {/* ============================================================ */}
      <section style={{ position: 'relative', borderTop: `1px solid ${C.border}` }}>
        <div
          style={{
            maxWidth: 880,
            margin: '0 auto',
            padding: isSmall ? '80px 24px' : '120px 32px',
            position: 'relative',
          }}
        >
          {/* Section index */}
          {!isMobile && (
            <div
              style={{
                position: 'absolute',
                right: -40,
                top: 120,
                writingMode: 'vertical-rl',
                fontFamily: F.body,
                fontSize: 11,
                letterSpacing: 4,
                textTransform: 'uppercase',
                color: C.textVeryDim,
                fontWeight: 600,
              }}
            >
              01 / 05 · The Point
            </div>
          )}

          <div
            style={{
              width: 80,
              height: 1,
              background: C.red,
              marginBottom: 28,
            }}
          />

          <Eyebrow>The Point</Eyebrow>

          <h2
            style={{
              fontFamily: F.display,
              fontSize: isSmall ? 56 : isMobile ? 76 : 96,
              letterSpacing: 1.5,
              lineHeight: 0.95,
              margin: '16px 0 40px 0',
              color: C.text,
            }}
          >
            WE NEVER LEFT<span style={{ color: C.red }}>.</span>
          </h2>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 24,
              fontFamily: F.body,
              fontSize: isSmall ? 17 : 19,
              lineHeight: 1.6,
              color: C.text,
              fontWeight: 500,
              maxWidth: 720,
            }}
          >
            <p style={{ margin: 0 }}>
              Valve moved on. The skins changed, the engine changed, the feel changed. A lot of us
              didn&apos;t follow.
            </p>
            <p style={{ margin: 0, color: C.textDim }}>
              Repeaked is built by — and for — the players who still believe CS:GO is the better
              game. Old engine. Real tick rate. A ranking ladder that takes itself seriously.
              No subscriptions. No competitive paywalls. No CS2.
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FEATURES */}
      {/* ============================================================ */}
      <section id="features" style={s.section}>
        <Eyebrow>The Systems</Eyebrow>
        <h2 style={s.sectionHeading}>
          EVERYTHING YOU NEED<span style={{ color: C.red }}>.</span>
          <br />
          NOTHING YOU DON&apos;T<span style={{ color: C.red }}>.</span>
        </h2>

        <div
          style={{
            marginTop: 56,
            display: 'grid',
            gridTemplateColumns: isSmall ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 16,
          }}
        >
          {FEATURES.map((f, i) => (
            <FeatureCard key={f.title} title={f.title} body={f.body} index={i} />
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/* RANKS */}
      {/* ============================================================ */}
      <section
        id="ranks"
        style={{
          borderTop: `1px solid ${C.border}`,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <div style={s.section}>
          <Eyebrow>The Ladder</Eyebrow>
          <h2 style={s.sectionHeading}>
            ELEVEN RANKS<span style={{ color: C.red }}>.</span> ONE GRIND<span style={{ color: C.red }}>.</span>
          </h2>
          <p style={s.sectionSubhead}>
            Climb from Rust to Repeaked. K-factor of 24 for veterans, 40 for new players, 16 above
            2500 ELO — so high ranks mean something. Win-streak weighting at 3, 5, and 7 in a row.
            Everyone starts at 1000.
          </p>

          <div
            style={{
              marginTop: 48,
              display: 'flex',
              flexWrap: isSmall ? 'wrap' : 'nowrap',
              justifyContent: isSmall ? 'center' : 'space-between',
              gap: 0,
              borderTop: `1px solid ${C.border}`,
              borderLeft: `1px solid ${C.border}`,
            }}
          >
            {RANKS.map(r => (
              <div
                key={r.id}
                style={{
                  flex: isSmall ? '0 0 25%' : '1',
                  borderRight: `1px solid ${C.border}`,
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <RankBadge rank={r} isMax={r.id === 11} />
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 24,
              display: 'flex',
              gap: 32,
              flexWrap: 'wrap',
              fontFamily: F.body,
              fontSize: 12,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: C.textDim,
              fontWeight: 600,
            }}
          >
            <span>◆ Starting ELO: 1000</span>
            <span>◆ Wingman + 5v5 separate ladders</span>
            <span>◆ Decay: none</span>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* ECONOMY */}
      {/* ============================================================ */}
      <section id="economy" style={s.section}>
        <Eyebrow>The Economy</Eyebrow>
        <h2 style={s.sectionHeading}>
          EARN<span style={{ color: C.red }}>.</span> OPEN<span style={{ color: C.red }}>.</span> EQUIP<span style={{ color: C.red }}>.</span>
        </h2>
        <p style={s.sectionSubhead}>
          Win matches, earn Fragz, open crates in the Weapons Dealer shop. Upgrade with StatTrak
          kits and Nametags. Shop skins are always Factory New — drops have random floats.
        </p>

        {/* System overview — 3 items */}
        <div
          style={{
            marginTop: 56,
            display: 'grid',
            gridTemplateColumns: isSmall ? '1fr' : 'repeat(3, 1fr)',
            border: `1px solid ${C.border}`,
          }}
        >
          {[
            { num: '01', title: 'FRAGZ',        body: 'Earn from match wins and drops. Top up with bundles from £0.99.' },
            { num: '02', title: 'CRATES',        body: 'Five rarity tiers. Rotating daily in the Weapons Dealer shop at 00:00 UTC.' },
            { num: '03', title: 'UPGRADE KITS',  body: 'StatTrak Kit (200 Fragz) · Nametag Kit (100 Fragz).' },
          ].map((item, i, arr) => (
            <div
              key={item.num}
              style={{
                display: 'flex',
                gap: 18,
                padding: '28px 24px',
                background: C.card,
                borderRight: i < arr.length - 1 && !isSmall ? `1px solid ${C.border}` : 'none',
                borderBottom: isSmall && i < arr.length - 1 ? `1px solid ${C.border}` : 'none',
              }}
            >
              <div
                style={{
                  fontFamily: F.display,
                  fontSize: 44,
                  lineHeight: 1,
                  color: C.red,
                  flexShrink: 0,
                  opacity: 0.6,
                  letterSpacing: -1,
                }}
              >
                {item.num}
              </div>
              <div style={{ paddingTop: 4 }}>
                <div
                  style={{
                    fontFamily: F.display,
                    fontSize: 20,
                    letterSpacing: 1.5,
                    color: C.text,
                    marginBottom: 6,
                    lineHeight: 1,
                  }}
                >
                  {item.title}
                </div>
                <div
                  style={{
                    fontFamily: F.body,
                    fontSize: 14,
                    color: C.textDim,
                    lineHeight: 1.5,
                  }}
                >
                  {item.body}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Crate tiers — clean list */}
        <div
          style={{
            marginTop: 16,
            border: `1px solid ${C.border}`,
          }}
        >
          <div
            style={{
              padding: '10px 24px',
              background: C.cardAlt,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <span
              style={{
                fontFamily: F.body,
                fontSize: 10,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: C.textVeryDim,
                fontWeight: 700,
              }}
            >
              Crate Grades
            </span>
          </div>
          {CRATES.map((c, i) => (
            <CrateTierRow key={c.label} crate={c} last={i === CRATES.length - 1} />
          ))}
        </div>

        {/* Premium Pass strip */}
        <div
          style={{
            marginTop: 16,
            padding: isSmall ? '24px 22px' : '28px 32px',
            background: C.card,
            border: `1px solid ${C.border}`,
            borderLeft: `3px solid ${C.orange}`,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: isMobile ? 'flex-start' : 'center',
            justifyContent: 'space-between',
            gap: 20,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: F.body,
                fontSize: 10,
                letterSpacing: 3,
                color: C.orange,
                fontWeight: 700,
                marginBottom: 6,
              }}
            >
              OPTIONAL
            </div>
            <div
              style={{
                fontFamily: F.display,
                fontSize: 22,
                letterSpacing: 1.2,
                color: C.text,
                marginBottom: 4,
              }}
            >
              PREMIUM PASS
            </div>
            <div style={{ fontFamily: F.body, fontSize: 14, color: C.textDim, lineHeight: 1.5 }}>
              Better drop rates, cosmetic profile flair, and supporter status. £4.99/mo — doesn&apos;t touch your rank.
            </div>
          </div>
          <SecondaryButton size="sm" onClick={() => navigate('/register')}>
            Learn More
          </SecondaryButton>
        </div>
      </section>

      {/* ============================================================ */}
      {/* COMMUNITY */}
      {/* ============================================================ */}
      <section id="community" style={s.section}>
        <Eyebrow>The People</Eyebrow>
        <h2 style={s.sectionHeading}>
          FOUND YOUR PEOPLE<span style={{ color: C.red }}>.</span>
        </h2>
        <p style={s.sectionSubhead}>
          Pre-launch, the Discord is where it all happens — beta keys, scrim pickups, feature
          requests, and patch notes. The X account is where the loud stuff lives.
        </p>

        <div
          style={{
            marginTop: 48,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 16,
          }}
        >
          <SocialCard
            platform="discord"
            headline="JOIN THE DISCORD"
            body="Beta access, scrim finder, patch notes, direct line to the dev. Where the community actually lives."
            cta="Join Server"
            href={DISCORD_URL}
            accent={C.red}
          />
          <SocialCard
            platform="x"
            headline="@REPEAKEDCSGO"
            body="Updates, giveaways, dev clips, and unfiltered build progress. Follow for the road to launch."
            cta="Follow on X"
            href={TWITTER_URL}
            accent={C.orange}
          />
        </div>

        <div
          style={{
            marginTop: 32,
            padding: '20px 0',
            borderTop: `1px solid ${C.border}`,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            color: C.textDim,
            fontFamily: F.body,
            fontSize: 14,
            fontStyle: 'italic',
            letterSpacing: 0.5,
          }}
        >
          <Diamond size={10} color={C.red} hollow={false} />
          Built by one player. Growing with the community that stayed.
        </div>
      </section>

      {/* ============================================================ */}
      {/* FINAL CTA */}
      {/* ============================================================ */}
      <section
        style={{
          position: 'relative',
          textAlign: 'center',
          padding: isSmall ? '100px 20px' : '140px 32px',
          background: `radial-gradient(ellipse at center, ${C.bgWarmer}, ${C.bg} 70%)`,
          borderTop: `1px solid ${C.border}`,
          overflow: 'hidden',
        }}
      >
        {/* Bookend diamonds */}
        {!isMobile && (
          <>
            <Diamond
              size={120}
              color={C.red}
              style={{
                position: 'absolute',
                left: '8%',
                top: '50%',
                transform: 'translate(-50%, -50%) rotate(45deg)',
                opacity: 0.08,
              }}
            />
            <Diamond
              size={120}
              color={C.orange}
              style={{
                position: 'absolute',
                right: '8%',
                top: '50%',
                transform: 'translate(50%, -50%) rotate(45deg)',
                opacity: 0.08,
              }}
            />
          </>
        )}
        <div style={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', marginBottom: 22 }}>
          <Eyebrow>Ready Up</Eyebrow>
        </div>
        <h2
          style={{
            position: 'relative',
            fontFamily: F.display,
            fontSize: isSmall ? 100 : isMobile ? 150 : 220,
            letterSpacing: -2,
            lineHeight: 0.86,
            margin: 0,
            color: C.text,
          }}
        >
          QUEUE<span style={{ color: C.red }}>.</span>
        </h2>
        <p
          style={{
            position: 'relative',
            fontFamily: F.body,
            fontSize: isSmall ? 16 : 19,
            color: C.textDim,
            margin: '24px 0 36px 0',
            fontWeight: 500,
            letterSpacing: 1,
          }}
        >
          Free. No credit card. No CS2.
        </p>
        <div style={{ position: 'relative' }}>
          <PrimaryButton size="lg" onClick={() => navigate('/register')}>
            Create Account <span style={{ marginLeft: 6 }}>→</span>
          </PrimaryButton>
        </div>
      </section>

      {/* ============================================================ */}
      {/* FOOTER */}
      {/* ============================================================ */}
      <footer
        style={{
          borderTop: `1px solid ${C.border}`,
          background: C.bg,
          padding: isSmall ? '40px 20px' : '60px 32px 40px',
        }}
      >
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'flex-start' : 'flex-end',
              gap: 32,
              paddingBottom: 32,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <div>
              <LogoLockup size={22} />
              <div
                style={{
                  marginTop: 12,
                  fontFamily: F.body,
                  fontSize: 14,
                  color: C.textDim,
                  fontStyle: 'italic',
                }}
              >
                The return to peak CS.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <FooterSocial href={DISCORD_URL} glyph="discord" />
              <FooterSocial href={TWITTER_URL} glyph="x" />
            </div>
          </div>

          <div
            style={{
              padding: '32px 0',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr) 2fr',
              gap: 32,
            }}
          >
            <FooterColumn
              title="Product"
              links={[
                ['Features', '#features'],
                ['Ranks', '#ranks'],
                ['Economy', '#economy'],
                ['Community', '#community'],
              ]}
            />
            <FooterColumn
              title="Community"
              links={[
                ['Discord', DISCORD_URL, true],
                ['X / Twitter', TWITTER_URL, true],
              ]}
            />
            <FooterColumn
              title="Legal"
              links={[
                ['Terms', '#'],
                ['Privacy', '#'],
                ['Cookies', '#'],
              ]}
            />
            {!isMobile && <div />}
          </div>

          <div
            style={{
              paddingTop: 24,
              borderTop: `1px solid ${C.border}`,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: 12,
              fontFamily: F.body,
              fontSize: 12,
              letterSpacing: 1,
              color: C.textVeryDim,
              fontWeight: 500,
            }}
          >
            <span>© 2026 Repeaked. All rights reserved.</span>
            <span>Built in London. For the players who stayed.</span>
          </div>
        </div>
      </footer>

      </div> {/* end content wrapper */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components used only in main
// ---------------------------------------------------------------------------
const NavLink = ({ href, label }) => {
  const [hover, hp] = useHover();
  const handleClick = (e) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        const y = el.getBoundingClientRect().top + window.scrollY - 70; // offset for sticky nav
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };
  return (
    <a
      href={href}
      onClick={handleClick}
      {...hp}
      style={{
        fontFamily: F.body,
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: 2.5,
        textTransform: 'uppercase',
        color: hover ? C.text : C.textDim,
        textDecoration: 'none',
        position: 'relative',
        padding: '8px 2px',
        transition: 'color 160ms ease',
      }}
    >
      {label}
      <span
        style={{
          position: 'absolute',
          left: 0,
          bottom: 4,
          height: 1,
          width: hover ? '100%' : '0%',
          background: C.red,
          transition: 'width 220ms ease',
        }}
      />
    </a>
  );
};

const FooterSocial = ({ href, glyph }) => {
  const [hover, hp] = useHover();
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      {...hp}
      aria-label={glyph}
      style={{
        width: 42,
        height: 42,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1px solid ${hover ? C.red : C.borderBright}`,
        background: hover ? C.cardAlt : 'transparent',
        transition: 'background 160ms ease, border-color 160ms ease',
      }}
    >
      <Glyph name={glyph} size={16} color={hover ? C.red : C.textDim} />
    </a>
  );
};

const FooterColumn = ({ title, links }) => (
  <div>
    <div
      style={{
        fontFamily: F.body,
        fontSize: 11,
        letterSpacing: 3,
        textTransform: 'uppercase',
        color: C.text,
        fontWeight: 700,
        marginBottom: 14,
      }}
    >
      {title}
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {links.map(([label, href, external]) => (
        <FooterLink key={label} label={label} href={href} external={external} />
      ))}
    </div>
  </div>
);

const FooterLink = ({ label, href, external }) => {
  const [hover, hp] = useHover();
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      {...hp}
      style={{
        fontFamily: F.body,
        fontSize: 13,
        color: hover ? C.text : C.textDim,
        textDecoration: 'none',
        transition: 'color 160ms ease',
        cursor: 'pointer',
      }}
    >
      {label}
    </a>
  );
};